import { navigate } from "@reach/router"
import { getAllPages } from "@shared/platform-api/requests/createRequest"
import { ApiError } from "@shared/primitive-components"
import { usePaginatedRequest } from "@shared/primitive-components/src/Table"
import { FindFleetModernInput, FleetEntryInput, Status } from "@shared/types"
import { downloadAsCsv, getCsvFileName } from "@shared/utils/helpers/downloadAsCsv"
import React, { useEffect, useMemo, useState } from "react"
import { unwrap } from "@shared/utils/helpers/unwrap"
import { FleetEntry as LegacyFleetEntry, findModernPaginatedFleetAsLegacy, WithVrns } from "../../api/fleet"
import { ListWrapperWithContext } from "../../components/ListWrapperWithContext"
import { usePolicyAndFleet, useAuth } from "../../contexts"
import { usePolicyAccessCapabilities } from "../../hooks/usePolicyAccessCapabilities"
import { AskJayBar, FleetListCharts, FleetListTable, TEST_IDS } from "./components"
import { DATA_TEST_IDS, FILTER_ITEMS } from "./config"
import { FleetListData, formatForCSV, makeData } from "./fleetListHandlers"
import { ListWrapperProps } from "./types"
import { PrevPolicyDropdown } from "../../ui/components"

interface FleetListModernProps {
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>
  setSelectedRows: React.Dispatch<React.SetStateAction<ReadonlyArray<FleetListData>>>
  selectedRows: ReadonlyArray<FleetListData>
  reloadTime: number | null
}

export const useListFleetWrapperProps = (
  props: Omit<ListWrapperProps, "title" | "subTitle" | "aboveTable" | "dataTestIds">
): ListWrapperProps => ({
  title: `Fleet overview`,
  subTitle: `Your Fleet`,
  aboveTable: (
    <>
      <FleetListCharts />
      <AskJayBar />
    </>
  ),
  dataTestIds: DATA_TEST_IDS,
  PreviousPolicyDropdown: <PrevPolicyDropdown />,
  filterLabel: `Cover status`,
  ...props,
})

export const FleetListModern: React.FC<FleetListModernProps> = ({
  setIsEditing,
  selectedRows,
  setSelectedRows,
  reloadTime,
}) => {
  const { policy, connectivity } = usePolicyAndFleet()
  const [isDownloading, setIsDownloading] = useState(false)
  const { policyHasAccessTo } = usePolicyAccessCapabilities()
  const auth = useAuth()

  const { loading, page, dataCount, search, mutateRequestInput, pagination, error, refresh } = usePaginatedRequest<
    WithVrns<FindFleetModernInput>,
    LegacyFleetEntry
  >({
    input: {
      id: policy.id,
      limit: 10,
      modern: true,
      coverStatus: `on_cover`,
      include: `make,model,abiGroup,type,fuelType,trim,grossWeight`,
      connectedVrns: connectivity.data?.connectedVrns ?? [],
    },
    // @ts-ignore @todo fix type..
    request: async (input) => {
      const { data } = await findModernPaginatedFleetAsLegacy({ ...input, search: input.term }, async () =>
        unwrap(await auth.getToken())
      )

      return data
    },
  })

  const filter = useMemo<ListWrapperProps[`filter`]>(
    () => ({
      defaultDisplayValue: `On Cover`,
      value: mutateRequestInput?.currentRequestInput?.coverStatus ?? null,
      action: (value: Status | null) => {
        mutateRequestInput.mutate({
          ...mutateRequestInput.currentRequestInput,
          coverStatus: value ?? undefined,
        })
      },
      firstItem: {
        text: `On cover`,
        value: `on_cover`,
        dataTestId: TEST_IDS.ON_COVER,
      },
      items: FILTER_ITEMS,
      dataTestIds: {
        button: TEST_IDS.FILTER,
      },
    }),
    [mutateRequestInput]
  )

  const isPolicyBound = useMemo(() => policy.status === `bound`, [policy.status])
  const enableSelectionColumn = useMemo(
    () => isPolicyBound && policyHasAccessTo(`EDIT_COVER_DATES`),
    [isPolicyBound, policyHasAccessTo]
  )

  const button = useMemo<ListWrapperProps[`button`]>(
    () =>
      policyHasAccessTo(`ADD_VEHICLES`)
        ? {
            action: () => navigate(`add`),
            text: `Add vehicles`,
            disabled: !isPolicyBound,
          }
        : undefined,
    [isPolicyBound, policyHasAccessTo]
  )

  const buttonLeft = useMemo<ListWrapperProps[`buttonLeft`]>(
    () =>
      policyHasAccessTo(`EDIT_COVER_DATES`)
        ? {
            action: () => {
              setIsEditing(true)
            },
            text: `Edit cover dates`,
            disabled: !selectedRows.length || !isPolicyBound,
            variant: `outline`,
          }
        : undefined,

    [selectedRows.length, isPolicyBound, setIsEditing, policyHasAccessTo]
  )

  const downloadFile = useMemo<ListWrapperProps[`downloadFile`]>(
    () => ({
      actionTitle: isDownloading ? `Exporting...` : `Export Fleet Schedule`,
      callback: async () => {
        setIsDownloading(true)
        const fleetData = await getAllPages<WithVrns<FleetEntryInput>, LegacyFleetEntry>(
          findModernPaginatedFleetAsLegacy,
          async () => unwrap(await auth.getToken()),
          {
            id: policy.id,
            include: `make,model,abiGroup,type,fuelType,trim,grossWeight,createdAt`,
            limit: 199,
            coverStatus: filter?.value ?? undefined,
            search: search.value,
            connectedVrns: connectivity.data?.connectedVrns ?? [],
          }
        )
        if (fleetData.tag === `ok`) {
          const filename = getCsvFileName(`Vehicles`, policy.policyHolder, policy.policyNumber)
          downloadAsCsv(fleetData.data.map(formatForCSV), filename)
        }
        setIsDownloading(false)
      },
    }),
    [filter?.value, isDownloading, policy.id, policy.policyHolder, policy.policyNumber, search.value]
  )

  const listWrapperProps = useListFleetWrapperProps({
    pageApiStatus: {
      loading,
      failed: false,
    },
    dataCount,
    buttonLeft,
    downloadFile,
    button,
    filter,
    search,
  })

  useEffect(() => {
    if (reloadTime) refresh()
    // we dont want to trigger again on refresh change. Only listen for reloadTime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTime])

  if (error) {
    return <ApiError />
  }
  return (
    <ListWrapperWithContext<Status | null> {...listWrapperProps}>
      <FleetListTable
        loading={loading}
        enableSelectionColumn={enableSelectionColumn}
        dataCount={dataCount}
        onSetSelectedRowsChanged={setSelectedRows}
        selectedRows={selectedRows}
        page={makeData(page)}
        pagination={pagination}
        // Temp - API is not setup to handle sorting yet.
        sortData={() => {}}
        key="FleetListModern"
      />
    </ListWrapperWithContext>
  )
}
